import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import Header from "@/components/Header";
import ChatPanel from "@/components/mentis/ChatPanel";
import Workspace from "@/components/mentis/Workspace";
import { api } from "@/lib/api";

const PRESETS = {
  cosmetics: {
    goal: "I've worked in cosmetics marketing for 5 years and want to launch my own skincare brand.",
    background: "I'm a marketing lead with five years in beauty — brand strategy, consumer research and go-to-market.",
    chips: ["branding", "consumer-research", "sales"],
    answers: [
      "I'd check the emulsifier system and the process — shear and temperature control at scale, since the bench formula worked.",
      "I'd run stability at 40°C, review the emulsifier HLB and phase ratio, and check the preservative system.",
      "I'd need substantiation data and to follow EU CPNP and US MoCRA rules before making that claim.",
      "I'd need GMP batch records, QC release testing and full traceability before a retailer stocks us.",
    ],
    mods: ["I already know branding. Remove it.", "Replace it with AI for Manufacturing.", "I eventually want to sell in the US."],
  },
  "ai-pm": {
    goal: "I want to become an AI Product Manager.",
    background: "I'm a software engineer with six years' experience who has shipped several product features.",
    chips: ["product-strategy", "analytics", "machine-learning"],
    answers: [
      "I'd first decide if ML is even needed versus rules, then check we have labelled data and a clear success metric.",
      "I'd look at data drift and labelling quality first — the eval set probably doesn't match production.",
      "I'd ground it with RAG over our docs, then build an eval set and a quality bar before shipping.",
      "Drift monitoring and a retraining trigger should have caught it — I'd add monitoring and retrain now.",
    ],
    mods: ["I want more entrepreneurship.", "I eventually want to work in fintech."],
  },
  finance: {
    goal: "I want to move into investment banking.",
    background: "I'm an analyst with three years at a bank, working on financial models and client materials.",
    chips: ["accounting", "modelling", "markets"],
    answers: [
      "For a profitable company I'd lean on DCF and trading comps; for the loss-making one I'd use growth-adjusted and revenue multiples.",
      "I'd check the cash/debt plug and any circular references — the balance sheet usually breaks at the financing line.",
      "Accretive means EPS goes up, but financing mix and synergies can make it misleading — I'd diligence those.",
      "I'd confirm market and credit risk limits, approvals and any regulatory obligations before it goes live.",
    ],
    mods: ["I want more AI in finance.", "I eventually want to work at a hedge fund."],
  },
  manufacturing: {
    goal: "I want to scale my manufacturing career.",
    background: "I'm a production supervisor with seven years on the shop floor leading line teams.",
    chips: ["production", "lean", "planning"],
    answers: [
      "I'd attack work-in-progress and flow first with value-stream mapping, not just output targets.",
      "I'd add safety stock selectively and dual-source, balancing service level against inventory cost.",
      "I'd instrument the biggest quality and downtime drivers first, then close the loop into decisions.",
      "I'd use SPC and structured root-cause to find the systemic driver, then build a preventive control.",
    ],
    mods: ["I want more AI on the shop floor.", "I eventually want to start my own operation."],
  },
  business: {
    goal: "I want to build and launch my own startup.",
    background: "I'm a product marketer with four years' experience and a startup idea I've been validating.",
    chips: ["idea", "customers", "marketing"],
    answers: [
      "I'd anchor pricing to the value metric users care about, test willingness to pay, then pick a model that scales.",
      "I'd fix week-one activation before spending more on ads, and track retention as the real signal.",
      "35% at pre-seed is steep — I'd weigh dilution against value-add and negotiate terms and board rights.",
      "We'd need incorporation, founder vesting, IP assignment and clean cap-table basics before building further.",
    ],
    mods: ["I want to add an AI product angle.", "I eventually want to raise venture funding."],
  },
};

export default function Build() {
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState(location.state?.session || null);
  const [sending, setSending] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [matching, setMatching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [presenting, setPresenting] = useState(false);
  const presentingRef = useRef(false);

  const sid = session?.id || localStorage.getItem("mentis_sid");
  const pendingSentRef = useRef(false);

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const sendPending = (id, msg) => {
    // Optimistically show the learner's message + thinking state while Mentis processes.
    setSession((s) => (s ? { ...s, messages: [...(s.messages || []), { role: "user", content: msg }], ui: null } : s));
    setSending(true);
    api.message(id, msg)
      .then(setSession)
      .catch(() => {
        setSession((s) => ({
          ...s,
          ui: { kind: "free" },
          messages: [...(s?.messages || []), {
            role: "mentis",
            content: "I couldn't connect just now — please type your goal again below and I'll pick it right up.",
          }],
        }));
      })
      .finally(() => setSending(false));
  };

  const runPresent = async (domain) => {
    const preset = PRESETS[domain] || PRESETS.cosmetics;
    presentingRef.current = true;
    setPresenting(true);
    const s = await api.createSession();
    localStorage.setItem("mentis_sid", s.id);
    setSession(s);
    const id = s.id;
    const step = async (fn, delay) => {
      if (!presentingRef.current) return;
      const r = await fn();
      if (!presentingRef.current) return;
      setSession(r);
      await wait(delay);
    };
    await step(() => api.message(id, preset.goal), 4200);
    await step(() => api.message(id, preset.background), 4200);
    await step(() => api.worked(id, preset.chips), 3000);
    await step(() => api.assessmentStart(id), 3600);
    for (const a of preset.answers) await step(() => api.message(id, a), 4600);
    if (presentingRef.current) {
      const r = await api.generatePathway(id); setSession(r);
      setProcessing(true); await wait(2800); setProcessing(false); await wait(2500);
    }
    for (const m of preset.mods) await step(() => api.message(id, m), 5200);
    if (presentingRef.current) {
      const r = await api.programMatch(id); setSession(r);
      setMatching(true); await wait(1900); setMatching(false);
    }
    presentingRef.current = false;
    setPresenting(false);
  };

  const stopPresent = () => {
    presentingRef.current = false;
    setPresenting(false);
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const present = params.get("present");
    if (present) { runPresent(present); return; }
    const querySid = params.get("sid");
    if (querySid) {
      localStorage.setItem("mentis_sid", querySid);
      api.getSession(querySid).then(setSession).catch(() => navigate("/"));
      return;
    }
    const pending = location.state?.pendingMessage;
    if (session && pending && !pendingSentRef.current) {
      pendingSentRef.current = true;
      localStorage.setItem("mentis_sid", session.id);
      sendPending(session.id, pending);
      return;
    }
    if (session) { localStorage.setItem("mentis_sid", session.id); return; }
    const stored = localStorage.getItem("mentis_sid");
    if (!stored) { navigate("/"); return; }
    api.getSession(stored).then(setSession).catch(() => navigate("/"));
  }, []); // eslint-disable-line

  const run = useCallback(async (fn) => {
    setSending(true);
    try { const r = await fn(); setSession(r); } finally { setSending(false); }
  }, []);

  const handlers = {
    onSend: (text) => run(() => api.message(sid, text)),
    onLinkedin: (body) => run(() => api.linkedin(sid, body)),
    onConfirmProfile: (body) => run(() => api.confirmProfile(sid, body)),
    onWorked: (selected) => run(() => api.worked(sid, selected)),
    onDecision: (action, comp) => run(() => api.decision(sid, { action, comp })),
    onReplace: (removed, comp) => run(() => api.applyPathway(sid, { action: "replace", removed, comp })),
    onAdd: (comp) => run(() => api.applyPathway(sid, { action: "add", comp })),
    onSubmitApp: (form) => run(() => api.applicationSubmit(sid, form)),
    onUploadCV: (file) => run(() => api.uploadCV(sid, file)),
    onAction: async (action) => {
      if (action === "pathway_generate") {
        const r = await api.generatePathway(sid); setSession(r);
        setProcessing(true); setTimeout(() => setProcessing(false), 2600);
        return;
      }
      if (action === "program_match") {
        const r = await api.programMatch(sid); setSession(r);
        setMatching(true); setTimeout(() => setMatching(false), 1900);
        return;
      }
      if (action === "assessment_start") return run(() => api.assessmentStart(sid));
      if (action === "application_start") return run(() => api.applicationStart(sid));
    },
  };

  const onReset = async () => { const r = await api.reset(sid); setSession(r); setSelected(null); };

  if (!session) {
    return <div className="App min-h-screen flex items-center justify-center text-slate-700">Loading Mentis…</div>;
  }

  return (
    <div className="App h-screen flex flex-col overflow-hidden">
      <Header variant="build" onReset={onReset} />
      {presenting && (
        <div data-testid="present-banner"
          className="bg-orange-500/12 border-b border-orange-500/25 px-5 py-2.5 flex items-center justify-between">
          <span className="mono-label text-[10px] text-orange-700 flex items-center gap-2">
            <span className="dot w-1.5 h-1.5 rounded-full bg-orange-400" /> Present mode · Mentis is building live
          </span>
          <button data-testid="present-exit" onClick={stopPresent}
            className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-slate-900 transition-colors">
            <X size={13} /> Exit
          </button>
        </div>
      )}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <div className="w-full lg:w-[40%] xl:w-[38%] border-b lg:border-b-0 lg:border-r border-black/[0.08] h-[55vh] lg:h-auto">
          <ChatPanel session={session} sending={sending} handlers={handlers} autoSpeak={presenting} />
        </div>
        <div className="w-full lg:w-[60%] xl:w-[62%] flex-1 bg-[#FAF9F7] overflow-hidden">
          <Workspace session={session} processing={processing} matching={matching}
            selected={selected} setSelected={setSelected} />
        </div>
      </div>
    </div>
  );
}
